import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
/**
 * Ajax client to gate all GET calls. While a GET request is in-flight, we will not send any others with the same url. This class is meant to be used
 * as a layer within our ajax request system.
 *
 * Optionally, a positive `cacheWindowMs` also serves repeat GETs for the same URL from the settled response for
 * that many milliseconds instead of hitting the network; a window <= 0 preserves the legacy in-flight-only dedup.
 */ export class GatedGetAjaxClient {
    executeRequest(url, options) {
        if (options.method != null && options.method !== "GET") {
            return this.innerAjaxClient.executeRequest(url, options);
        }
        let entry = this.cachedGetEntries[url];
        if (entry != null) {
            // Tally the hit on the entry itself; the aggregate is logged once when the entry is evicted.
            if (entry.settled) {
                entry.windowedHits++;
            } else {
                entry.inFlightHits++;
            }
        } else {
            this.cachedGetEntries[url] = entry = {
                settled: false,
                inFlightHits: 0,
                windowedHits: 0,
                apiName: options.telemetryConfig?.apiName ?? "unknown",
                promise: this.innerAjaxClient.executeRequest(url, options).finally(()=>{
                    entry.settled = true;
                    // Keep a settled response cached for the window so rapid repeat requests are served from it; otherwise evict immediately
                    if (this.cacheWindowMs > 0) {
                        setTimeout(()=>this.evict(url, entry), this.cacheWindowMs);
                    } else {
                        this.evict(url, entry);
                    }
                })
            };
        }
        // A Response body is a one-shot stream, so windowed callers each get an independently readable clone
        // (clone only runs on success, leaving cached rejections untouched); legacy mode returns the exact
        // shared promise, preserving prior object identity.
        return this.cacheWindowMs > 0 ? entry.promise.then((response)=>response.clone()) : entry.promise;
    }
    /**
     * Removes the cache entry and, if any dedup hits accumulated on it, emits a single aggregated trace for that
     * GET, tagged with the logical API name. The URL is intentionally omitted to avoid logging potentially
     * sensitive query data. No-op logging when no logger context was provided or the entry saw no dedup hits.
     */ evict(url, entry) {
        delete this.cachedGetEntries[url];
        if (this.loggers == null || entry.inFlightHits + entry.windowedHits === 0) {
            return;
        }
        this.loggers.traceLogger.logTrace(0x1d80c603 /* tag_36myd */ , TraceLevel.Info, `GatedGetAjaxClient GET dedup hits [Api=${entry.apiName}][InFlightHits=${entry.inFlightHits}][WindowedHits=${entry.windowedHits}][CacheWindowMs=${this.cacheWindowMs}]`);
    }
    constructor(innerAjaxClient, cacheWindowMs = 0, loggers){
        this.innerAjaxClient = innerAjaxClient;
        this.cacheWindowMs = cacheWindowMs;
        this.loggers = loggers;
        this.cachedGetEntries = {};
    }
}
